import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  {
    label: 'Services',
    href: '/services/asbestos-abatement',
    sub: [
      { label: 'Asbestos Abatement', href: '/services/asbestos-abatement' },
      { label: 'Mold Remediation', href: '/services/asbestos-abatement' },
      { label: 'Hazardous Material Removal', href: '/services/asbestos-abatement' },
      { label: 'Environmental Consulting', href: '/services/asbestos-abatement' },
    ],
  },
  { label: 'About', href: '/about', sub: [] },
  { label: 'Locations', href: '/locations/colorado-springs', sub: [] },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <>
      {/* Emergency top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-ecos-green h-9 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-alarm-warning-line text-white text-xs"></i>
            </div>
            <span className="text-white text-xs font-medium tracking-wide">24/7 Emergency Response Available</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="mailto:info@ecosenvironmental.com" className="hidden md:flex items-center gap-1.5 text-white/80 hover:text-white text-xs transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-line text-xs"></i>
              info@ecosenvironmental.com
            </a>
            <a href="tel:+18888685420" className="flex items-center gap-1.5 text-white font-semibold text-xs transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-xs"></i>
              888-868-5420
            </a>
          </div>
        </div>
      </div>

      <header
        className={`fixed top-9 left-0 right-0 z-40 transition-all duration-400 ${
          scrolled
            ? 'bg-ecos-charcoal/98 backdrop-blur-sm border-b border-white/8'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-18"
          style={{ height: '72px' }}>

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://www.ecosenvironmental.com/images/brand/logo-light.2504231044093.png"
              alt="ECOS Environmental & Disaster Restoration"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div
                key={link.href}
                className="relative"
                onMouseEnter={() => link.sub.length > 0 && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className="flex items-center gap-1 text-white/75 hover:text-white text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer py-2"
                >
                  {link.label}
                  {link.sub.length > 0 && (
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-sm text-white/40"></i>
                    </div>
                  )}
                </Link>

                {link.sub.length > 0 && activeDropdown === link.label && (
                  <div className="absolute top-full left-0 bg-ecos-charcoal border border-white/10 rounded-sm py-2 min-w-52 shadow-2xl">
                    {link.sub.map((sub) => (
                      <Link
                        key={sub.href + sub.label}
                        to={sub.href}
                        className="block px-5 py-2.5 text-white/65 hover:text-white hover:bg-white/5 text-sm transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+18888685420"
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-ecos-green"></i>
              </div>
              Call Now
            </a>
            <Link
              to="/#assessment"
              className="bg-ecos-green hover:bg-ecos-green-light text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors duration-200 cursor-pointer whitespace-nowrap tracking-wide"
            >
              Free Assessment
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden text-white w-9 h-9 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-ecos-charcoal border-t border-white/10 px-6 py-6 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-white/75 hover:text-white py-3 text-base font-medium border-b border-white/8 transition-colors cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-5 flex flex-col gap-3">
              <a
                href="tel:+18888685420"
                className="flex items-center justify-center gap-2 border border-white/20 text-white py-3 rounded-sm text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-ecos-green"></i>
                888-868-5420
              </a>
              <Link
                to="/#assessment"
                className="bg-ecos-green text-white py-3 rounded-sm text-sm font-semibold text-center cursor-pointer whitespace-nowrap tracking-wide"
              >
                Request Free Assessment
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
